import type { AiFieldStreamTarget } from '@databases/ai-first-creation-token-processing';
import {
  fetchDatabaseAiTeammate,
  iterateDatabaseAiTeammateDocumentParts
} from '@databases/ai-teammate-client';
import { logError } from '@databases/logs';

import { DatabaseStreamState } from './database-stream-state';
import { readDatabaseContent } from './handlers/read-database-content-handler';

export type RunDatabaseAiFieldOptions = {
  contentId: string;
  locale: string;
  prompt: string;
  target: AiFieldStreamTarget;
  abortSignal?: AbortSignal;
};

export async function runDatabaseAiField({
  contentId,
  locale,
  prompt,
  target,
  abortSignal
}: RunDatabaseAiFieldOptions): Promise<void> {
  const streamState = DatabaseStreamState.getInstance();
  if (streamState.isStreamingActive) {
    throw new Error('Another database stream is already active');
  }

  const content = readDatabaseContent(contentId, {
    includeAiFields: true,
    includeSelection: false
  });
  if (content.contentType !== 'database') {
    throw new Error('Expected database content for AI field generation');
  }

  const reader = await fetchDatabaseAiTeammate(
    {
      prompt,
      locale,
      databaseContext: {
        content_id: contentId,
        title: content.title,
        url: content.url,
        csv: content.csv
      }
    },
    abortSignal
  );

  let streamCompletionScheduled = false;

  try {
    streamState.clearErrors();
    streamState.startAiFieldStreaming(contentId, target);

    for await (const documentPart of iterateDatabaseAiTeammateDocumentParts(reader)) {
      streamState.processChunk(documentPart);
    }

    await streamState.completeStreaming();
    streamCompletionScheduled = true;

    const errors = streamState.getErrors();
    if (errors.length > 0) {
      throw new Error(errors.map(({ message }) => message).join('; '));
    }

    streamState.markAiFieldRunCompleted();
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error) {
    // If completion has not scheduled its delayed reset, abort only this AI-field stream.
    if (!streamCompletionScheduled && streamState.isAiFieldStreamingActive) {
      streamState.abortStreaming();
    }

    try {
      await reader.cancel();
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (cancelError) {
      logError(
        'Failed to cancel database AI field stream',
        cancelError instanceof Error ? cancelError : new Error(String(cancelError))
      );
    }

    throw error;
  }
}
